import AdvanceTable from "components/common/advance-table/AdvanceTable";
import AdvanceTableFooter from "components/common/advance-table/AdvanceTableFooter";
import CardDropdown from "components/common/CardDropdown";
import { useAdvanceTable } from "hooks";
import useRoles from "hooks/access-control/useRoles";
import AdvanceTableProvider from "providers/AdvanceTableProvider";
import React, { useState } from "react";
import { Dropdown, Spinner } from "react-bootstrap";
import WidgetSectionTitle from "widgets/WidgetSectionTitle";
import { Empty } from "antd";
import IconButton from "components/common/IconButton";
import { ConfirmDeleteModal } from "components/common/ConfirmDeleteModal";
import CreateOrUpdateRoleModal from "./CreateOrUpdateRoleModal";
import { useNavigate } from "react-router-dom";
import { useAppContext } from "providers/AppProvider";
import Guard from "../rbac/Guard";
import { PRIV } from "../rbac/privileges";

const customStyles = {
  backgroundColor: "#e8f7e3",
  color: "#2e7d32",
  padding: "3px 8px",
  borderRadius: "6px",
  fontSize: "0.65rem",
  border: "1px solid #d5ebd0",
  cursor: "pointer",
  fontWeight: 600,
};

export default function RolesManagement() {
  const navigate = useNavigate();
  const { roles, addRole, updateRole, loading, deleteRoleById } = useRoles();
  const formatText = (text) => `${text.slice(0, 25)}...`;

  const [showAddModal, setShowAddModal] = useState(false);
  const [selectedRole, setSelectedRole] = useState(null);
  const [showUpdateModal, setShowUpdateModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [roleToDelete, setRoleToDelete] = useState(null);
  const [deletingRole, setDeletingRole] = useState(null);

  const { config } = useAppContext();
  const isCollapsed = config.isNavbarVerticalCollapsed;

  const handleUpdateRole = (id) => {
    const role = roles.find((r) => r.id === id);
    setSelectedRole(role);
    setShowUpdateModal(true);
  };

  const confirmDelete = (id) => {
    setRoleToDelete(id);
    setShowDeleteModal(true);
  };

  const handleDelete = async () => {
    try {
      setDeletingRole(roleToDelete);
      await deleteRoleById(roleToDelete);
    } finally {
      setDeletingRole(null);
    }
  };

  const columns = [
    {
      accessorKey: "name",
      header: "Name",
      meta: {
        headerProps: { className: "text-900" },
      },
    },
    {
      accessorKey: "description",
      header: "Description",
      cell: ({ getValue }) => {
        const desc = getValue();
        return desc.length > 25 ? formatText(desc) : desc;
      },
      meta: {
        headerProps: { className: "text-900" },
      },
    },
    {
      accessorKey: "privileges",
      header: "Privileges",
      cell: ({ getValue, row }) => {
        const privileges = getValue() || [];
        const limit = isCollapsed ? 9 : 5;
        if (privileges.length === 0) return "No privileges assigned"

        const displayed = privileges.slice(0, limit);
        const remaining = privileges.length - limit;
        return (
          <div className="d-flex flex-wrap gap-1">
            {displayed.map((privilege) => (
              <span key={privilege.id} style={customStyles}>
                {privilege.name}
              </span>
            ))}

            {remaining > 0 && (
              <span
                style={{
                  ...customStyles,
                  backgroundColor: "#fff3cd",
                  color: "#b7791f",
                  border: "1px solid #ffe8a1",
                }}
                onClick={() =>
                  navigate(`/access-control/roles/${row.original.id}`)
                }
              >
                +{remaining} more
              </span>
            )}
          </div>
        );
      },
      meta: {
        headerProps: { className: "text-900" },
      },
    },

    {
      id: "actions",
      header: "Actions",
      cell: ({ row }) => {
        const item = row.original;
        return (
          <CardDropdown drop="start">
            <div className="py-2">
              <Dropdown.Item
                onClick={() => navigate(`/access-control/roles/${item.id}`)}
              >
                View
              </Dropdown.Item>
              <Guard privilege={PRIV.UPDATE_ROLES}>
                <Dropdown.Item onClick={() => handleUpdateRole(item.id)}>
                  Edit
                </Dropdown.Item>
              </Guard>
              <Guard privilege={PRIV.DELETE_ROLES}>
                <Dropdown.Divider as="div" />
                <Dropdown.Item
                  onClick={() => confirmDelete(item.id)}
                  className="text-danger"
                >
                  Delete
                </Dropdown.Item>
              </Guard>
            </div>
          </CardDropdown>
        );
      },
      meta: {
        headerProps: { className: "text-900 text-center" },
        cellProps: { className: "text-center" },
      },
    },
  ];

  const table = useAdvanceTable({
    data: roles,
    columns,
    sortable: true,
    pagination: true,
    perPage: 10,
  });

  if (loading) {
    return (
      <div
        className="d-flex justify-content-center align-items-center"
        style={{ minHeight: 400 }}
      >
        <Spinner animation="border" variant="success" />
        <span className="ms-3" style={{ fontSize: "13.33px" }}>
          Loading...
        </span>
      </div>
    );
  }

  return (
    <>
      <WidgetSectionTitle
        icon="user-shield"
        title="Roles"
        subtitle="Manage user roles and control access across the platform."
        transform="shrink-2"
        className="mb-4 mt-3"
      />

      <Guard privilege={PRIV.CREATE_ROLES}>
        <IconButton
          variant="falcon-default"
          size="sm"
          icon="plus"
          transform="shrink-3"
          onClick={() => setShowAddModal(true)}
          className="text-xs mb-2"
        >
          <span className="ms-1">Add role</span>
        </IconButton>
      </Guard>

      {roles && roles.length > 0 ? (
        <AdvanceTableProvider {...table}>
          <AdvanceTable
            headerClassName="bg-200 text-nowrap align-middle"
            rowClassName="align-middle white-space-nowrap"
            tableProps={{
              bordered: true,
              striped: true,
              className: "text-xxs mb-0 overflow-hidden",
            }}
          />
          <div className="mt-3">
            <AdvanceTableFooter
              rowInfo
              navButtons
              rowsPerPageSelection
              rowsPerPageOptions={[10, 20, 50, 100, 200]}
            />
          </div>
        </AdvanceTableProvider>
      ) : (
        <div>
          <Empty />
        </div>
      )}

      <CreateOrUpdateRoleModal
        show={showAddModal}
        onHide={() => setShowAddModal(false)}
        onSave={addRole}
      />

      <CreateOrUpdateRoleModal
        show={showUpdateModal}
        onHide={() => setShowUpdateModal(false)}
        role={selectedRole} // role to update
        onSave={updateRole}
      />

      <ConfirmDeleteModal
        itemName="role"
        show={showDeleteModal}
        setShow={setShowDeleteModal}
        onConfirm={handleDelete}
        loading={deletingRole === roleToDelete}
      />
    </>
  );
}
